import { map, find } from "lodash";
import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectPipeSpecs,
  selectPipeSpecByNps,
  setPipeSpecs,
  fetchPipeSpecsAsync,
} from "./../redux/slices/pipeSpecsSlice";
import {
  selectMaterials,
  setMaterials,
  fetchMaterialsAsync,
} from "./../redux/slices/materialsSlice";
import {
  resetState,
  updateNps,
  updateSchedule,
  updateOutsideDiameter,
  updateInsideDiameter,
  updateWallThickness,
  updateMaterial,
  updateYieldStrength,
  updateTensileStrength,
  updateAllowableStress,
  updateDesignPressure,
  updateTestPressure,
  updateDesignCode,
  updateB318Construction,
  updateLocationClass,
  updateDesignFactor,
  updateJointFactor,
  updateDesignTemperature,
  updateCorrosionAllowance,
} from "./../redux/slices/calculatorSlice";
import {
  B318_CONSTRUCTIONS,
  B318_LOCATION_CLASSES,
  JOINT_FACTORS,
  isPipelineCode,
} from "./../utils/pipelineWall";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  Chip,
  Alert,
} from "@mui/material";

// The PipeCalculator component renders a form for selecting pipe
// characteristics, material properties and design pressures.  It
// computes minimum required wall thicknesses in real time using
// ASME/ASME formulas.  Data sets are lazily loaded from Firebase
// realtime database if available; otherwise the local JSON
// definitions bundled in the application are used.  Redux slices
// maintain the current selections and computed results.

const PipeCalculator = () => {
  const dispatch = useDispatch();

  const pipeSpecs = useSelector((state) => state.pipeSpecs.specs);
  const materials = useSelector((state) => state.materials.materials);

  const calculator = useSelector((state) => state.calculator);
  const pipelineBasis = isPipelineCode(calculator.designCode);
  const pipeline = calculator.pipeline;

  // On mount, attempt to fetch pipe specs and materials from Firebase.
  useEffect(() => {
    async function loadData() {
      await dispatch(fetchPipeSpecsAsync());
      await dispatch(fetchMaterialsAsync());
    }
    loadData();
  }, [dispatch]);

  // Generate list of available schedules for the selected NPS
  const scheduleOptions = useMemo(() => {
    if (!calculator.selectedNps) return [];
    const spec = find(
      pipeSpecs,
      (s) => s.nps.toLowerCase() === calculator.selectedNps.toLowerCase()
    );
    if (!spec) return [];
    const schedules = spec.schedules || {};
    return Object.keys(schedules).filter((key) => schedules[key] != null);
  }, [pipeSpecs, calculator.selectedNps]);

  // Determine the selected spec for this NPS
  const selectedSpec = useMemo(() => {
    if (!calculator.selectedNps) return null;
    return find(
      pipeSpecs,
      (s) => s.nps.toLowerCase() === calculator.selectedNps.toLowerCase()
    );
  }, [pipeSpecs, calculator.selectedNps]);

  // Event handlers
  const handleNpsChange = (event) => {
    const nps = event.target.value;
    // Determine outside diameter from spec
    const spec = find(pipeSpecs, (s) => s.nps === nps);
    const od = spec ? spec.od : null;
    dispatch(updateNps({ nps, outsideDiameter: od }));
  };

  const handleScheduleChange = (event) => {
    const schedule = event.target.value;
    // Determine thickness from spec
    let thickness = null;
    if (selectedSpec && selectedSpec.schedules) {
      thickness = selectedSpec.schedules[schedule];
    }
    dispatch(updateSchedule({ schedule, wallThickness: thickness }));
  };

  const handleMaterialChange = (event) => {
    const materialName = event.target.value;
    const material = find(materials, (m) => m.name === materialName);
    dispatch(
      updateMaterial({
        materialName,
        yieldStrength: material ? material.yield : null,
        tensileStrength: material ? material.tensile : null,
        allowableStress: material ? material.allowable : null,
      })
    );
  };

  const handleCustomYieldChange = (event) => {
    dispatch(updateYieldStrength(event.target.value));
  };

  const handleCustomTensileChange = (event) => {
    dispatch(updateTensileStrength(event.target.value));
  };

  const handleCustomAllowableChange = (event) => {
    dispatch(updateAllowableStress(event.target.value));
  };

  const handleDesignPressureChange = (event) => {
    dispatch(updateDesignPressure(event.target.value));
  };

  const handleTestPressureChange = (event) => {
    dispatch(updateTestPressure(event.target.value));
  };

  const handleOutsideDiameterChange = (event) => {
    dispatch(updateOutsideDiameter(event.target.value));
  };

  const handleInsideDiameterChange = (event) => {
    dispatch(updateInsideDiameter(event.target.value));
  };

  const handleWallThicknessChange = (event) => {
    dispatch(updateWallThickness(event.target.value));
  };

  const handleReset = () => {
    dispatch(resetState());
  };

  const handleDesignCodeChange = (event) =>
    dispatch(updateDesignCode(event.target.value));
  const handleConstructionChange = (event) =>
    dispatch(updateB318Construction(event.target.value));
  const handleLocationClassChange = (event) =>
    dispatch(updateLocationClass(event.target.value));
  const handleDesignFactorChange = (event) =>
    dispatch(updateDesignFactor(event.target.value));
  const handleJointFactorChange = (event) =>
    dispatch(updateJointFactor(event.target.value));
  const handleDesignTemperatureChange = (event) =>
    dispatch(updateDesignTemperature(event.target.value));
  const handleCorrosionAllowanceChange = (event) =>
    dispatch(updateCorrosionAllowance(event.target.value));

  const inches = (v) => (v != null ? `${v.toFixed(3)} in` : "—");
  const psi = (v) =>
    v != null ? `${Math.round(v).toLocaleString()} psi` : "—";
  const pct = (v) => (v != null ? `${v.toFixed(1)}% SMYS` : "—");

  return (
    <Box sx={{ maxWidth: 900, margin: "0 auto", p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Pipe Wall Thickness Calculator
      </Typography>
      <Typography variant="body2" gutterBottom>
        Select a nominal pipe size and schedule, then choose a material and
        specify design pressure. Pick the design basis: the allowable-stress
        method (B31.3 / Section VIII) sizes the wall on the code allowable
        stress, roughly a third of tensile; the pipeline codes (B31.8, B31.4)
        size it on yield times a location-class design factor, on the nominal
        wall. You can also enter pipe dimensions and material properties by
        hand.
      </Typography>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            {/* Design basis */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="design-code-label">Design Basis</InputLabel>
                <Select
                  labelId="design-code-label"
                  value={calculator.designCode}
                  label="Design Basis"
                  onChange={handleDesignCodeChange}
                >
                  <MenuItem value="B31.3">
                    Allowable stress — ASME B31.3 / Section VIII
                  </MenuItem>
                  <MenuItem value="B31.8">
                    ASME B31.8 — gas transmission &amp; distribution
                  </MenuItem>
                  <MenuItem value="B31.4">
                    ASME B31.4 — liquid pipelines
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {calculator.designCode === "B31.8" && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="construction-label">
                      Construction
                    </InputLabel>
                    <Select
                      labelId="construction-label"
                      value={calculator.b318Construction}
                      label="Construction"
                      onChange={handleConstructionChange}
                    >
                      {map(B318_CONSTRUCTIONS, (c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="location-class-label">
                      Location Class
                    </InputLabel>
                    <Select
                      labelId="location-class-label"
                      value={calculator.locationClass}
                      label="Location Class"
                      onChange={handleLocationClassChange}
                    >
                      {map(B318_LOCATION_CLASSES, (c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            {pipelineBasis && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Design Factor F"
                    type="number"
                    fullWidth
                    value={calculator.designFactor ?? ""}
                    onChange={handleDesignFactorChange}
                    InputProps={{ inputProps: { min: 0, max: 1, step: 0.01 } }}
                    helperText={
                      calculator.designCode === "B31.8"
                        ? "Set from the construction and class; override for crossings, bridges or near people (Table 841.1.6-2)"
                        : "B31.4 §403.2.1 uses 0.72"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="joint-factor-label">
                      Longitudinal Joint Factor E
                    </InputLabel>
                    <Select
                      labelId="joint-factor-label"
                      value={calculator.jointFactorId}
                      label="Longitudinal Joint Factor E"
                      onChange={handleJointFactorChange}
                    >
                      {map(JOINT_FACTORS, (j) => (
                        <MenuItem key={j.id} value={j.id}>
                          {j.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {calculator.designCode === "B31.8" && (
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Design Temperature (°F)"
                      type="number"
                      fullWidth
                      value={calculator.designTemperature ?? ""}
                      onChange={handleDesignTemperatureChange}
                      error={calculator.temperatureFactor == null}
                      helperText={
                        calculator.temperatureFactor == null
                          ? "Above 450°F — outside Table 841.1.8-1"
                          : `T = ${calculator.temperatureFactor.toFixed(
                              3
                            )} (Table 841.1.8-1; 1.000 up to 250°F)`
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Corrosion Allowance (in)"
                    type="number"
                    fullWidth
                    value={calculator.corrosionAllowance || ""}
                    onChange={handleCorrosionAllowanceChange}
                    InputProps={{ inputProps: { min: 0, step: 0.0625 } }}
                    helperText="Added to the pressure-design wall; taken off the checked wall for its MAOP"
                  />
                </Grid>
              </>
            )}
            {/* NPS Selection */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="nps-label">Nominal Pipe Size (NPS)</InputLabel>
                <Select
                  labelId="nps-label"
                  value={calculator.selectedNps || ""}
                  label="Nominal Pipe Size (NPS)"
                  onChange={handleNpsChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {map(pipeSpecs, (spec) => {
                    return (
                      <MenuItem key={spec.nps} value={spec.nps}>
                        {spec.nps}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            {/* Schedule Selection */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth disabled={!calculator.selectedNps}>
                <InputLabel id="schedule-label">Pipe Schedule</InputLabel>
                <Select
                  labelId="schedule-label"
                  value={calculator.selectedSchedule || ""}
                  label="Pipe Schedule"
                  onChange={handleScheduleChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {map(scheduleOptions, (sch) => (
                    <MenuItem key={sch} value={sch}>
                      {sch}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Outside Diameter */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Outside Diameter (in)"
                type="number"
                fullWidth
                value={calculator.outsideDiameter || ""}
                onChange={handleOutsideDiameterChange}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            {/* Inside Diameter */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Inside Diameter (in)"
                type="number"
                fullWidth
                value={calculator.insideDiameter || ""}
                onChange={handleInsideDiameterChange}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            {/* Wall Thickness */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Wall Thickness (in)"
                type="number"
                fullWidth
                value={calculator.wallThickness || ""}
                onChange={handleWallThicknessChange}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            {/* Material Selection */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="material-label">Material</InputLabel>
                <Select
                  labelId="material-label"
                  value={calculator.selectedMaterial || ""}
                  label="Material"
                  onChange={handleMaterialChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {map(materials, (mat) => {
                    return (
                      <MenuItem key={mat.name} value={mat.name}>
                        {mat.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            {/* Yield Strength */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label={
                  pipelineBasis
                    ? "Yield Strength — SMYS (psi)"
                    : "Yield Strength (psi)"
                }
                type="number"
                fullWidth
                value={calculator.yieldStrength || ""}
                onChange={handleCustomYieldChange}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            {/* Tensile Strength */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Tensile Strength (psi)"
                type="number"
                fullWidth
                value={calculator.tensileStrength || ""}
                onChange={handleCustomTensileChange}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            {/* Allowable Stress — allowable-stress basis only */}
            {!pipelineBasis && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Allowable Stress (psi)"
                  type="number"
                  fullWidth
                  value={calculator.allowableStress || ""}
                  onChange={handleCustomAllowableChange}
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText="Leave blank to compute as min(2/3*YS, 1/3*UTS)"
                />
              </Grid>
            )}
            {/* Design Pressure */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Design Pressure (psi)"
                type="number"
                fullWidth
                value={calculator.designPressure || ""}
                onChange={handleDesignPressureChange}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            {/* Test Pressure — the pipeline codes set their own minimum */}
            {!pipelineBasis && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Test Pressure (psi)"
                  type="number"
                  fullWidth
                  value={calculator.testPressure || ""}
                  onChange={handleTestPressureChange}
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText="If blank, 1.5× Design Pressure will be assumed"
                />
              </Grid>
            )}
            {/* Reset Button */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleReset}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {/* Results card */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Results
          </Typography>
          {pipelineBasis ? (
            pipeline ? (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  ASME {pipeline.code}: P = (2 · S · t / D) · F · E · T with S =
                  SMYS, t = nominal wall. F = {pipeline.designFactor}, E ={" "}
                  {pipeline.jointFactor}, T ={" "}
                  {pipeline.temperatureFactor.toFixed(3)}
                  {pipeline.corrosionAllowance
                    ? `, corrosion allowance ${pipeline.corrosionAllowance.toFixed(
                        4
                      )} in`
                    : ""}
                  .
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">
                      Wall for Pressure Design
                    </Typography>
                    <Typography variant="body1">
                      {inches(pipeline.requiredPressureWall)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">
                      Required Nominal Wall (incl. CA)
                    </Typography>
                    <Typography variant="body1">
                      {inches(pipeline.requiredNominalWall)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2">
                      Minimum Test Pressure
                    </Typography>
                    <Typography variant="body1">
                      {psi(pipeline.minTestPressure)}{" "}
                      <Typography component="span" variant="caption">
                        ({pipeline.testFactor}× design,{" "}
                        {pipeline.code === "B31.8"
                          ? "Table 841.3.2-1"
                          : "§437.4.1"}
                        )
                      </Typography>
                    </Typography>
                  </Grid>
                  {pipeline.maop != null && (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle2">
                          Selected Wall {inches(calculator.wallThickness)}
                        </Typography>
                        <Chip
                          size="small"
                          color={pipeline.adequate ? "success" : "error"}
                          label={
                            pipeline.adequate
                              ? `Adequate — ${(pipeline.margin * 100).toFixed(
                                  0
                                )}% over design pressure`
                              : "Too thin for the design pressure"
                          }
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle2">
                          MAOP of Selected Wall
                        </Typography>
                        <Typography variant="body1">
                          {psi(pipeline.maop)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle2">
                          Hoop Stress @ Design / @ Test
                        </Typography>
                        <Typography variant="body1">
                          {pct(pipeline.hoopStressPctSmys)} /{" "}
                          {pct(pipeline.testHoopStressPctSmys)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </>
            ) : (
              <Alert severity="info">
                Enter the outside diameter, yield strength (SMYS) and design
                pressure — and a design temperature of 450°F or less for B31.8.
              </Alert>
            )
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2">
                  Minimum Wall @ Working Pressure
                </Typography>
                <Typography variant="body1">
                  {calculator.minWallWorking != null
                    ? calculator.minWallWorking.toFixed(3) + " in"
                    : "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2">
                  Minimum Wall @ Test Pressure
                </Typography>
                <Typography variant="body1">
                  {calculator.minWallTest != null
                    ? calculator.minWallTest.toFixed(3) + " in"
                    : "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2">
                  ASME B31.3 Thickness
                </Typography>
                <Typography variant="body1">
                  {calculator.minWallB313 != null
                    ? calculator.minWallB313.toFixed(3) + " in"
                    : "—"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PipeCalculator;
